import { Link } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import ProfitSystemLogo from "../components/ProfitSystemLogo";

const PrivacyPolicy = () => {
  const accountDataItems = ["Nombre", "Email", "Empresa", "Datos de facturación"];
  const integrationDataItems = [
    "Datos de gasto publicitario",
    "Métricas de campañas (clics, impresiones, conversiones)",
    "Datos agregados de rendimiento",
    "Ingresos o eventos de conversión asociados"
  ];
  const noAccessItems = [
    "Contraseñas",
    "Datos personales de clientes finales",
    "Contenido privado no relacionado con métricas publicitarias"
  ];
  const usageItems = [
    "Proporcionar análisis de rendimiento y rentabilidad",
    "Calcular métricas financieras (ROAS, CAC, profit, etc.)",
    "Generar informes y dashboards",
    "Mejorar la calidad del servicio"
  ];
  const legalBasisItems = [
    "La ejecución del contrato del servicio.",
    "El consentimiento otorgado al conectar cuentas externas mediante OAuth.",
    "Cumplimiento de obligaciones legales."
  ];
  const securityItems = [
    "Cifrado de credenciales y tokens",
    "Separación de datos por cliente (multi-tenant)",
    "Accesos restringidos",
    "Infraestructura segura"
  ];
  const retentionItems = [
    "Los datos se conservarán mientras el usuario mantenga una cuenta activa.",
    "En caso de cancelación, los datos se eliminarán en un plazo máximo de 30 días, salvo obligación legal."
  ];
  const rightsItems = [
    "Derecho de acceso",
    "Rectificación",
    "Supresión",
    "Limitación",
    "Portabilidad",
    "Oposición"
  ];
  const revocationItems = [
    "La configuración de su cuenta",
    "El panel de la plataforma conectada",
    "O solicitándolo por email"
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <nav className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-nav-home-link">
            <ProfitSystemLogo height={40} />
          </Link>
          <Link to="/" className="text-sm font-medium text-slate-600 hover:text-red-600" data-testid="privacy-nav-back-link">
            Volver al inicio
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <header className="mb-12" data-testid="privacy-header">
          <p className="text-sm font-semibold text-red-600 uppercase tracking-wide" data-testid="privacy-kicker">
            Legal
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-3" data-testid="privacy-title">
            Política de Privacidad
          </h1>
          <p className="text-slate-600 mt-4" data-testid="privacy-subtitle">
            Última actualización: Febrero 2025
          </p>
        </header>

        <section className="space-y-4 mb-10" data-testid="privacy-section-1">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-1-title">1. Responsable del tratamiento</h2>
          <div className="space-y-2 text-slate-600">
            <p data-testid="privacy-section-1-item-1">Titular: Eleven Group EDT SLU</p>
            <p data-testid="privacy-section-1-item-2">CIF: B55230940</p>
            <p data-testid="privacy-section-1-item-3">Domicilio: Carrer onyar 65, 17457 Riudellots de la Selva</p>
            <p data-testid="privacy-section-1-item-4">Email de contacto: info@ecommsystem.net</p>
            <p data-testid="privacy-section-1-item-5">Sitio web: profit.ecommsystem.net</p>
            <p data-testid="privacy-section-1-item-6">En adelante, “la Empresa”.</p>
          </div>
        </section>

        <section className="space-y-6 mb-10" data-testid="privacy-section-2">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-2-title">2. Qué datos recopilamos</h2>
          <p className="text-slate-600" data-testid="privacy-section-2-intro">
            Recopilamos los siguientes tipos de datos:
          </p>
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-slate-900" data-testid="privacy-section-2-subtitle-1">2.1 Datos de cuenta</h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                {accountDataItems.map((item, index) => (
                  <li key={item} data-testid={`privacy-section-2-account-item-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-slate-900" data-testid="privacy-section-2-subtitle-2">2.2 Datos obtenidos mediante integraciones autorizadas</h3>
              <p className="text-slate-600" data-testid="privacy-section-2-integrations-intro">
                Cuando el usuario conecta cuentas de terceros (por ejemplo, plataformas publicitarias o de analítica), accedemos únicamente a los datos necesarios para prestar el servicio, como:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                {integrationDataItems.map((item, index) => (
                  <li key={item} data-testid={`privacy-section-2-integration-item-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="space-y-2">
              <p className="text-slate-600" data-testid="privacy-section-2-no-access-intro">No accedemos a:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                {noAccessItems.map((item, index) => (
                  <li key={item} data-testid={`privacy-section-2-noaccess-item-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="space-y-4 mb-10" data-testid="privacy-section-3">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-3-title">3. Finalidad del tratamiento</h2>
          <p className="text-slate-600" data-testid="privacy-section-3-intro">Los datos se utilizan exclusivamente para:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {usageItems.map((item, index) => (
              <li key={item} data-testid={`privacy-section-3-item-${index}`}>{item}</li>
            ))}
          </ul>
          <p className="text-slate-600 font-medium" data-testid="privacy-section-3-note">No vendemos ni cedemos datos a terceros.</p>
        </section>

        <section className="space-y-4 mb-10" data-testid="privacy-section-4">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-4-title">4. Base legal</h2>
          <p className="text-slate-600" data-testid="privacy-section-4-intro">La base legal para el tratamiento es:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {legalBasisItems.map((item, index) => (
              <li key={item} data-testid={`privacy-section-4-item-${index}`}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="space-y-4 mb-10" data-testid="privacy-section-5">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-5-title">5. Almacenamiento y seguridad</h2>
          <p className="text-slate-600" data-testid="privacy-section-5-intro">
            Implementamos medidas técnicas y organizativas adecuadas para proteger la información, incluyendo:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {securityItems.map((item, index) => (
              <li key={item} data-testid={`privacy-section-5-item-${index}`}>{item}</li>
            ))}
          </ul>
          <p className="text-slate-600" data-testid="privacy-section-5-note">
            Los tokens de acceso a plataformas externas se almacenan de forma cifrada.
          </p>
        </section>

        <section className="space-y-4 mb-10" data-testid="privacy-section-6">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-6-title">6. Conservación de datos</h2>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {retentionItems.map((item, index) => (
              <li key={item} data-testid={`privacy-section-6-item-${index}`}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="space-y-4 mb-10" data-testid="privacy-section-7">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-7-title">7. Derechos del usuario</h2>
          <p className="text-slate-600" data-testid="privacy-section-7-intro">El usuario puede ejercer:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {rightsItems.map((item, index) => (
              <li key={item} data-testid={`privacy-section-7-item-${index}`}>{item}</li>
            ))}
          </ul>
          <p className="text-slate-600" data-testid="privacy-section-7-contact">Solicitudes a: info@ecommsystem.net</p>
        </section>

        <section className="space-y-4 mb-10" data-testid="privacy-section-8">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-8-title">8. Eliminación de datos de terceros</h2>
          <p className="text-slate-600" data-testid="privacy-section-8-intro">
            El usuario puede revocar en cualquier momento el acceso a plataformas externas desde:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            {revocationItems.map((item, index) => (
              <li key={item} data-testid={`privacy-section-8-item-${index}`}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="space-y-4 mb-10" data-testid="privacy-section-9">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-9-title">9. Transferencias internacionales</h2>
          <p className="text-slate-600" data-testid="privacy-section-9-text">
            En caso de que existan transferencias internacionales de datos, se realizarán conforme a las garantías legales aplicables.
          </p>
        </section>

        <section className="space-y-4" data-testid="privacy-section-10">
          <h2 className="text-xl font-bold text-slate-900" data-testid="privacy-section-10-title">10. Cambios en esta política</h2>
          <p className="text-slate-600" data-testid="privacy-section-10-text">
            Nos reservamos el derecho a actualizar esta política para adaptarla a cambios normativos o mejoras del servicio.
          </p>
        </section>
      </main>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500" data-testid="privacy-footer-text">
            © 2025 ProfitSystem. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/terminos-y-condiciones" className="text-slate-500 hover:text-red-600" data-testid="privacy-footer-terms-link">
              Términos y condiciones
            </Link>
            <Link to="/politica-de-privacidad" className="text-slate-500 hover:text-red-600" data-testid="privacy-footer-privacy-link">
              Política de privacidad
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
